import { useEffect } from 'react';
import { authService } from '../services/auth';
import { isTrustedShellOrigin } from '../constants/origins';

interface AuthBridgeCallbacks {
  onAuthSuccess: () => void;
  onLoadingDone: () => void;
}

export function useAuthBridge({ onAuthSuccess, onLoadingDone }: AuthBridgeCallbacks): void {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('code')) return;

    const isInIframe = window !== window.parent;
    if (!isInIframe) {
      onLoadingDone();
      return;
    }

    if (authService.loadCachedIframeToken()) {
      onAuthSuccess();
      onLoadingDone();
    }

    let shellTokenReceived = false;

    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedShellOrigin(event.origin)) return;

      if (event.data?.type === 'BIZTADA_AUTH_TOKEN' && event.data?.token) {
        shellTokenReceived = true;
        authService.setAuthToken(
          event.data.token as string,
          event.data.businessId as string | undefined,
        );
        onAuthSuccess();
        onLoadingDone();
      }
    };

    const handleMessageWrapped = (event: MessageEvent) => {
      handleMessage(event);
      if (event.data?.type === 'BIZTADA_AUTH_TOKEN' && event.data?.token) {
        shellTokenReceived = true;
      }
    };

    window.addEventListener('message', handleMessageWrapped);

    const sendReady = () =>
      window.parent.postMessage(
        { type: 'IFRAME_READY', app: 'business-marketplace' },
        '*',
      );

    sendReady();
    let retries = 0;
    const retryInterval = setInterval(() => {
      if (shellTokenReceived) {
        clearInterval(retryInterval);
        return;
      }
      retries += 1;
      if (retries <= 30) sendReady();
      else clearInterval(retryInterval);
    }, 1000);

    const timeout = setTimeout(() => {
      if (!authService.isAuthenticated()) {
        onLoadingDone();
      }
    }, 10000);

    return () => {
      window.removeEventListener('message', handleMessageWrapped);
      clearInterval(retryInterval);
      clearTimeout(timeout);
    };
  }, [onAuthSuccess, onLoadingDone]);
}
